"use client";

import type { LanguageOrAccent } from "@mw/shared/languagesAndAccents";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Button, Dialog, Heading, Modal, ModalOverlay, Tabs, TabPanel } from "react-aria-components";
import { clsx } from "clsx";
import { TabList } from "@components/core/TabList";

import { LanguageItemWithHover } from "./BlockLanguagesAccentsGlobe";

type Tab = "languages" | "accents";

interface LanguagesAccentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab: Tab;
  languages: LanguageOrAccent[];
  accents: LanguageOrAccent[];
  mode?: "text-to-speech-link" | "none";
  previewUrls?: Record<string, string>;
}

export function LanguagesAccentsModal({
  isOpen,
  onClose,
  initialTab,
  languages,
  accents,
  mode,
  previewUrls,
}: LanguagesAccentsModalProps) {
  const t = useTranslations("ClientKeys.languages_accents_globe");
  const tGlobals = useTranslations("ClientKeys.Globals");
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  // Sync initial tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      isDismissable
      className={({ isEntering, isExiting }) =>
        clsx(
          "tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/10",
          "tw-transition-opacity tw-duration-200 tw-ease-out",
          isEntering && "tw-animate-in tw-fade-in",
          isExiting && "tw-animate-out tw-fade-out",
        )
      }
    >
      <Modal
        className={({ isEntering, isExiting }) =>
          clsx(
            "tw-mx-4 tw-flex tw-h-[80vh] tw-max-h-[600px] tw-w-full tw-max-w-sm tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-border tw-shadow-xl tw-outline-none border-overlay-dark bg-eggshell",
            "tw-transition-all tw-duration-200 tw-ease-out",
            isEntering && "tw-animate-in tw-fade-in tw-zoom-in-95",
            isExiting && "tw-animate-out tw-fade-out tw-zoom-out-95",
          )
        }
      >
        <Dialog className='tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden tw-outline-none'>
          {/* Modal header - centered title */}
          <div className='tw-px-6 tw-pt-8 tw-pb-6'>
            <Heading slot='title' className='tw-text-center tw-text-balance tw-max-w-sm tw-mx-auto f-heading-04'>
              {t("modal_title_all")}
            </Heading>
          </div>

          {/* Tabs using react-aria-components for a11y */}
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={key => setActiveTab(key as Tab)}
            className='tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden'
          >
            {/* Tab switcher */}
            <div className='tw-px-4 tw-py-1 tw-mb-6 tw-mx-auto'>
              <TabList
                aria-label={t("modal_tabs_aria")}
                items={[
                  { id: "languages", title: t("tab_languages") },
                  { id: "accents", title: t("tab_accents") },
                ]}
                surface='cream'
                size='lg'
              />
            </div>

            {/* Tab panels - scrollable list with full-width items */}
            <TabPanel id='languages' className='tw-flex-1 tw-overflow-y-auto'>
              <ul className='tw-flex tw-flex-col'>
                {languages.map((item, index) => (
                  <LanguageItemWithHover
                    key={item.value}
                    item={item}
                    showDottedBorder={index < languages.length - 1}
                    fullWidth
                    mode={mode}
                    previewUrl={previewUrls?.[item.slug]}
                  />
                ))}
              </ul>
            </TabPanel>
            <TabPanel id='accents' className='tw-flex-1 tw-overflow-y-auto'>
              <ul className='tw-flex tw-flex-col'>
                {accents.map((item, index) => (
                  <LanguageItemWithHover
                    key={item.value}
                    item={item}
                    showDottedBorder={index < accents.length - 1}
                    fullWidth
                    mode={mode}
                    previewUrl={previewUrls?.[item.slug]}
                  />
                ))}
              </ul>
            </TabPanel>
          </Tabs>

          {/* Modal footer - entire area is clickable to close */}
          <Button
            onPress={onClose}
            className='tw-flex tw-w-full tw-cursor-pointer tw-justify-center tw-bg-gray-100 tw-py-4 tw-transition-colors hover:tw-bg-gray-200 hover:tw-text-gray-900 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-gray-400 f-paragraph-01'
          >
            {tGlobals("close")}
          </Button>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
